import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, NavLink } from "react-router-dom";
import { put, post } from '../../../utils/api';
import { Form, FormLine, FormItem, FormInput } from '../../common/Form';
import { activityDate2Human } from '../../../utils/dateTime';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faAsterisk } from '@fortawesome/free-solid-svg-icons';
import './ActivityForm.css';
import Translator from '../../common/Translator';
const LIMIT_NAME = 40;
const LIMIT_DESCR = 300;
const LIMIT_SPEAKER = 40;
//
// Основной компонент для редактирования активности
//
const ActivityForm = ({ exhibitor, activity }) => {
	const { t, i18n } = useTranslation();
	const lang = i18n.language === 'en' ? 'en' : 'ru';
	const navigate = useNavigate();
	const [ date, setDate ] = useState('');
	const [ dates, setDates ] = useState([]);
	const [ hours, setHours ] = useState([]);
	const [ mins, setMins ] = useState([]);
	const [ startHor, setStartHor ] = useState('');
	const [ startMin, setStartMin ] = useState('');
	const [ stopHor, setStopHor ] = useState('');
	const [ stopMin, setStopMin ] = useState('');
	const [ name_ru, setNameRu ] = useState(activity ? activity.name.ru : '');
	const [ name_en, setNameEn ] = useState(activity ? activity.name.en : '');
	const [ descr_ru, setDescrRu ] = useState(activity ? activity.descr.ru : '');
	const [ descr_en, setDescrEn ] = useState(activity ? activity.descr.en : '');
	const [ speaker_ru, setSpeakerRu ] = useState(activity ? activity.speaker.ru : '');
	const [ speaker_en, setSpeakerEn ] = useState(activity ? activity.speaker.en : '');
    const [ isSaving, setIsSaving ] = useState(false);
	const [ isTranslating, setIsTranslating ] = useState(false);
    const [ saveError, setSaveError ] = useState('');

	// Заполняем список опция для выбора минут
	// Мы рассчитываем на то, что минуты в установках мероприятия указаны с тем же шагом что и тут.
	// Если вдруг понадобится шаг кратный минуте - ну тогда поменяем.
	useEffect(() => {
		const mins = [];
		for(let i = 0; i < 60; i += 5) {
			const value = `0${i}`.slice(-2);
			mins.push({ text: value, value });
		}
		setMins(mins);
	}, []);

	// Заполняем список опций для выбора дня
	// NB: Используем полную строку даты т.к. по ней проверяем выход времени за пределы рабочего дня
	useEffect(() => {
		if(!Array.isArray(exhibitor.event.dates)) {
			setDates([]);
			return;
		}
		let dates = [];
		exhibitor.event.dates.forEach(date => {
			dates.push({ value: date, text: activityDate2Human(date, lang) });
		});
		dates.sort((a, b) => a.value.localeCompare(b.value));
		setDates(dates);
	}, [ exhibitor, lang ]);

	// Установка выбранной даты и времени при редактировании
	// NB: Мы ищем дату в списке до первого совпадения.
	//     Но может быть несколько одинаковых дат с разными временами проведения.
	//     Тогда в теории мы можем выбрать дату с неверной парой начала и конца.
	//     В целом это маловероятно и такого быть не должно.
	useEffect(() => {
		setDate('');
		if(!activity || !dates.length) return;
		const [ date, start, stop ] = activity.date.split(' ');
		dates.forEach(d => {
			if(d.value.startsWith(date)) {
				// Заполним список часов
				const hours = [];
				for(let i = parseInt(d.value.split(' ')[1]); i <= parseInt(d.value.split(' ')[2]); i ++) {
					const value = `0${i}`.slice(-2);
					hours.push({ text: value, value });
				}
				// Устанавливаем все значения
				setDate(d.value);
				setHours(hours);
				setStartHor(start.split(':')[0]);
				setStartMin(start.split(':')[1]);
				setStopHor(stop.split(':')[0]);
				setStopMin(stop.split(':')[1]);
			}
		});
	}, [ dates, activity ]);

	// Выбор даты
	// Подготавливаем список часов для выбора времени активности
	const onDateSelect = (date) => {
		if(!date) {
			setDate('');
			setHours([]);
			setStartHor('');
			setStopHor('');
			setStartMin('');
			setStopMin('');
			return;	
		}
		// eslint-disable-next-line 
		const [ d, start, stop ] = date.split(' ');
		const hours = [];
		for(let i = parseInt(start); i <= parseInt(stop); i ++) {
			const value = `0${i}`.slice(-2);
			hours.push({ text: value, value });
		}
		setDate(date);
		setHours(hours);
		setStartHor(hours[0].value);
		setStopHor(hours[hours.length - 1].value);
		// Установим минуты
		// Мы рассчитываем на то, что минуты в установках мероприятия указаны с тем же шагом что и тут
		setStartMin(start.split(':')[1]);
		setStopMin(stop.split(':')[1]);
	};

	// Проверка того, что время не выходит за пределы даты мероприятия
	// Возвращает откорректированную пару в виде массива
	const checkTime = (hor, min) => {
		if(!date) return [ '','' ]; // при невыбранной дате любое время некорректно
		const time = `${hor}:${min}`;
		// eslint-disable-next-line 
		const [ d, start, stop ] = date.split(' ');
		if(time < start) return start.split(':');
		if(time > stop) return stop.split(':');
		return [ hor, min ];
	};

	// Выбор времени начала
	// Должен быть раньше времени окончания и в пределах дня
	const onTimeStartSelect = (hor, min) => {
		[hor, min] = checkTime(hor, min);
		setStartHor(hor);
		setStartMin(min);
		const start = `${hor}:${min}`;
		const stop = `${stopHor}:${stopMin}`;
		if(start > stop) {
			setStopHor(hor);
			setStopMin(min);	
		}
	};

	// Выбор времени окончания
	// Должен быть позже времени начала и в пределах дня
	const onTimeStopSelect = (hor, min) => {
		[hor, min] = checkTime(hor, min);
		setStopHor(hor);
		setStopMin(min);
		const start = `${startHor}:${startMin}`;
		const stop = `${hor}:${min}`;
		if(start > stop) {
			setStartHor(hor);
			setStartMin(min);	
		}
	};

    // Отправка данных
	const onSubmit = async () => {
		if(isSaving || isTranslating) return;
        setIsSaving(true);
        setSaveError('');

        const data = {
			date: `${date.split(' ')[0]} ${startHor}:${startMin} ${stopHor}:${stopMin}`,
            name_ru,
            name_en,
            descr_ru,
            descr_en,
            speaker_ru,
            speaker_en,
			exhibitor: exhibitor._id,
        };

        let result;
        if(activity) {
            result = await put('/activities/' + activity._id, data);
        } else {
            result = await post('/activities', data);
        }

        if(result.error) {
            setSaveError(result.error);
        } else {
			navigate(`/cabinet/exhibitorview/${exhibitor._id}/action/updated`);
        }

        setIsSaving(false);
	};

    return (
		<div>
	    	<h1>{activity ? t('activityform.title_edit') : t('activityform.title_add')}</h1>
			{isSaving ? <span>{t('common.loading')}</span> :
			<Form
                error={saveError}
                info={activity ? 'activityform.subtitle_edit' : 'activityform.subtitle_add'}
                wide={true}
                submit={activity ? t('activityform.submit_edit') : t('activityform.submit_add')}
                onSubmit={onSubmit}
            >
				<FormLine>
					<FormItem>
						<FormInput
							id='name_ru'
							type='text'
							limit={LIMIT_NAME}
							value={name_ru}
							onChange={setNameRu}
							placeholder={t('activityform.name_ru_hint')}
							className='lang-ru'
							label={t('activityform.name_ru')}
							required={true}
							disabled={isSaving || isTranslating}
							hint={<Translator current={name_ru} text={name_en} from='en' limit={LIMIT_NAME} setText={setNameRu} setIsTranslating={setIsTranslating}/>}
						/>
					</FormItem>
					<FormItem>
						<FormInput
							id='name_en'
							type='text'
							limit={LIMIT_NAME}
							value={name_en}
							onChange={setNameEn}
							placeholder={t('activityform.name_en_hint')}
							className='lang-en'
							label={t('activityform.name_en')}
							required={true}
							disabled={isSaving || isTranslating}
							hint={<Translator current={name_en} text={name_ru} from='ru' limit={LIMIT_NAME} setText={setNameEn} setIsTranslating={setIsTranslating}/>}
						/>
					</FormItem>
				</FormLine>
				<FormLine>
					<FormItem>
						<FormInput
							id='speaker_ru'
							type='text'
							limit={LIMIT_SPEAKER}
							value={speaker_ru}
							onChange={setSpeakerRu}
							placeholder={t('activityform.speaker_ru_hint')}
							className='lang-ru'
							label={t('activityform.speaker_ru')}
							disabled={isSaving || isTranslating}
							hint={<Translator current={speaker_ru} text={speaker_en} from='en' limit={LIMIT_SPEAKER} setText={setSpeakerRu} setIsTranslating={setIsTranslating}/>}
						/>
					</FormItem>
					<FormItem>
						<FormInput
							id='speaker_en'
							type='text'
							limit={LIMIT_SPEAKER}
							value={speaker_en}
							onChange={setSpeakerEn}
							placeholder={t('activityform.speaker_en_hint')}
							className='lang-en'
							label={t('activityform.speaker_en')}
							disabled={isSaving || isTranslating}
							hint={<Translator current={speaker_en} text={speaker_ru} from='ru' limit={LIMIT_SPEAKER} setText={setSpeakerEn} setIsTranslating={setIsTranslating}/>}
						/>
					</FormItem>
				</FormLine>
				<FormLine>
					<FormItem>
						<FormInput
							id='date'
							type='select'
							value={date}
							list={dates}
							onChange={onDateSelect}
							label={t('activityform.date')}
							required={true}
						/>
					</FormItem>
					<FormItem>
						<label>
							<Icon icon={faAsterisk} className='required-star'/>
							{t('activityform.time')}
						</label>
						<div className='Range'>
							<FormInput
								id='startHor'
								type='select'
								value={startHor}
								list={hours}
								onChange={hor => onTimeStartSelect(hor, startMin)}
								required={true}
							/>
							<div>:</div>
							<FormInput
								id='startMin'
								type='select'
								value={startMin}
								list={mins}
								onChange={min => onTimeStartSelect(startHor, min)}
								required={true}
							/>
							<div>-</div>
							<FormInput
								id='stopHor'
								type='select'
								value={stopHor}
								list={hours}
								onChange={hor => onTimeStopSelect(hor, stopMin)}
								required={true}
							/>
							<div>:</div>
							<FormInput
								id='stopMin'
								type='select'
								value={stopMin}
								list={mins}
								onChange={min => onTimeStopSelect(stopHor, min)}
								required={true}
							/>
						</div>
					</FormItem>
				</FormLine>
				<FormLine>
					<FormItem>
						<FormInput
							id='descr_ru'
							type='textarea'
							limit={LIMIT_DESCR}
							value={descr_ru}
							onChange={setDescrRu}
							placeholder={t('activityform.descr_ru_hint')}
							className='lang-ru'
							label={t('activityform.descr_ru')}
							disabled={isSaving || isTranslating}
							hint={<Translator current={descr_ru} text={descr_en} from='en' limit={LIMIT_DESCR} setText={setDescrRu} setIsTranslating={setIsTranslating}/>}
						/>
					</FormItem>
					<FormItem>
						<FormInput
							id='descr_en'
							type='textarea'
							limit={LIMIT_DESCR}
							value={descr_en}
							onChange={setDescrEn}
							placeholder={t('activityform.descr_en_hint')}
							className='lang-en'
							label={t('activityform.descr_en')}
							disabled={isSaving || isTranslating}
							hint={<Translator current={descr_en} text={descr_ru} from='ru' limit={LIMIT_DESCR} setText={setDescrEn} setIsTranslating={setIsTranslating}/>}
						/>
					</FormItem>
				</FormLine>
			</Form>}
			
			<NavLink to={'/cabinet/exhibitorview/' + exhibitor._id}>
				{t('exhibitoredit.back')}
			</NavLink>
		</div>
	);
};

export default ActivityForm;
